import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LegalMarkdown } from '../../legal/LegalMarkdown.js';
import '../../pages/legal.css';
import './consent.css';

/**
 * Peças do aviso em camadas: na tela fica só o essencial, discreto; o texto
 * completo abre na hora, sem tirar a pessoa do que ela estava fazendo.
 */

export type LegalDoc = 'termos' | 'privacidade' | 'termo-negocio';

const TITLE: Record<LegalDoc, string> = {
  termos: 'Termos de uso',
  privacidade: 'Política de Privacidade',
  'termo-negocio': 'Termo de tratamento de dados',
};

const SOURCE: Record<LegalDoc, () => Promise<{ default: string }>> = {
  termos: () => import('../../legal/termos.md?raw'),
  privacidade: () => import('../../legal/privacidade.md?raw'),
  'termo-negocio': () => import('../../legal/termo-negocio.md?raw'),
};

/** Folha com o texto legal inteiro, por cima da tela atual. */
export function LegalSheet({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  const [source, setSource] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void SOURCE[doc]().then((module) => alive && setSource(module.default));
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
    };
  }, [doc, onClose]);
  return createPortal(
    <div className="lgs-back" onClick={onClose}>
      <div
        className="lgs"
        role="dialog"
        aria-modal="true"
        aria-label={TITLE[doc]}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lgs-head">
          <b>{TITLE[doc]}</b>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6 6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="lgs-body lg-body">
          {source ? <LegalMarkdown source={source} /> : <p className="lgs-wait">Abrindo…</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Link discreto que abre um texto legal na folha. */
export function DocLink({ doc, children }: { doc: LegalDoc; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="cs-link"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
      >
        {children}
      </button>
      {open && <LegalSheet doc={doc} onClose={() => setOpen(false)} />}
    </>
  );
}

/** "Ver detalhes": o texto completo abre ali mesmo, embaixo da linha curta. */
export function Details({
  children,
  label = 'Ver detalhes',
}: {
  children: ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="cs-more"
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        {open ? 'Fechar detalhes' : label}
      </button>
      {open && <div className="cs-details">{children}</div>}
    </>
  );
}

/** Linha pequena de apoio, com detalhes opcionais. */
export function FinePrint({
  children,
  details,
  className = '',
}: {
  children: ReactNode;
  details?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`cs-fine ${className}`}>
      <p>
        {children}
        {details && (
          <>
            {' '}
            <Details>{details}</Details>
          </>
        )}
      </p>
    </div>
  );
}

/** Caixa "Li e aceito": desmarcada até a pessoa marcar. */
export function ConsentCheck({
  checked,
  onChange,
  children,
  details,
  tone = 'light',
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
  details?: ReactNode;
  tone?: 'light' | 'dark';
}) {
  return (
    <div className={`cs-check ${tone}${checked ? ' on' : ''}`}>
      <label>
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="cs-box" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14">
            <path
              d="m5 12.5 4.5 4.5L19 7.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="cs-text">{children}</span>
      </label>
      {details && (
        <div className="cs-check-more">
          <Details>{details}</Details>
        </div>
      )}
    </div>
  );
}
